import React from 'react'
import {useTranslation} from 'react-i18next'
import {BoardCard, isFinalCrmStage, TeamMember} from '../../domain/entities/Pipeline.ts'
import {cardTravelerName, dayCounter, memberById, nextActionLabel, relativeDue, TONE, useDateFormatters} from './pipelineView.ts'
import {Avatar, ChannelIcon, Countries, CrmBadges, MarketChip, NextActionWho, StepDots} from './PipelineParts.tsx'

// "Code expires in Nd": only inside the last 60 days, red under 30.
const EXPIRY_CHIP_DAYS = 60
const EXPIRY_RED_DAYS = 30

type ActivationCardProps = {
    card: BoardCard
    team: TeamMember[]
    selected: boolean
    dragging: boolean
    onOpen: (card: BoardCard) => void
    onMarkLost: (card: BoardCard) => void
    onDragStart: (card: BoardCard, e: React.DragEvent) => void
    onDragEnd: (e: React.DragEvent) => void
}

export const ActivationCard: React.FC<ActivationCardProps> = ({card, team, selected, dragging, onOpen, onMarkLost, onDragStart, onDragEnd}) => {
    const {t} = useTranslation('dashboard')
    const {formatTripRange} = useDateFormatters()
    const name = cardTravelerName(card, t)
    const isFinal = isFinalCrmStage(card.crmStage)
    const counter = dayCounter(card.trip, t)
    const accountManager = memberById(team, card.accountManagerId)
    const days = card.daysToDeadline
    const showExpiry = !isFinal && days != null && days > 0 && days <= EXPIRY_CHIP_DAYS
    const due = card.nextAction ? relativeDue(card.nextAction.dueAt, t) : null
    const isWait = card.nextAction?.type === 'WAIT'

    return (
        <div
            draggable={!isFinal}
            onDragStart={(e) => onDragStart(card, e)}
            onDragEnd={onDragEnd}
            className={`group relative flex-none rounded-xl border bg-white transition-colors duration-200 ease-out ${
                selected ? 'border-neutralBlack-900' : 'border-neutralBlack-200 hover:border-neutralBlack-400'
            } ${dragging ? 'opacity-40' : ''} ${isFinal ? '' : 'cursor-grab active:cursor-grabbing'}`}
        >
            <button type="button" onClick={() => onOpen(card)}
                    aria-label={t('pipelines.card.open', {name})}
                    className="block w-full text-left p-3.5 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutralBlack-900">
                <span className="flex items-center gap-1.5 pr-7">
                    <Countries countries={card.trip.countries}/>
                    <span className="ml-0.5 text-[15px] font-bold tracking-[-.01em] text-neutralBlack-900 whitespace-nowrap">
                        {formatTripRange(card.trip.startDate, card.trip.endDate)}
                    </span>
                </span>
                <span className="block mt-2.5 text-[14px] font-medium text-neutralBlack-900 truncate">{name}</span>
                <span className="flex items-center gap-2 mt-1.5 min-w-0">
                    <Avatar name={card.agencyName ?? card.agencyId} size={22}/>
                    <span className="text-[12px] text-neutralBlack-600 truncate min-w-0">{card.agencyName ?? card.agencyId}</span>
                    {card.accountManagerId && (
                        <span className="ml-auto flex-none inline-flex items-center"
                              title={t('pipelines.card.accountManager', {name: accountManager?.name ?? card.accountManagerId})}>
                            <span className="sr-only">{t('pipelines.card.accountManager', {name: accountManager?.name ?? card.accountManagerId})}</span>
                            <Avatar member={accountManager} name={card.accountManagerId} size={20}/>
                        </span>
                    )}
                </span>

                <span className="flex items-center flex-wrap gap-1.5 mt-3">
                    {!isFinal && <ChannelIcon channel={card.traveler.preferredChannel}/>}
                    <MarketChip market={card.market}/>
                    {counter && (
                        <span className="inline-flex items-center h-5 px-2 rounded-md bg-neutralBlack-100 text-[11px] font-semibold text-neutralBlack-700">{counter}</span>
                    )}
                    {showExpiry && (
                        <span className={`inline-flex items-center h-5 px-2 rounded-md text-[11px] font-semibold ${days! < EXPIRY_RED_DAYS ? TONE.red : TONE.amber}`}>
                            {t('pipelines.card.expiresIn', {count: days!})}
                        </span>
                    )}
                </span>

                {(card.flags.contactFailed || card.flags.pausedUntil || card.alerts.length > 0) && (
                    <span className="block mt-2">
                        <CrmBadges contactFailed={card.flags.contactFailed} contactFailedReason={card.flags.contactFailedReason}
                                   pausedUntil={card.flags.pausedUntil} alerts={card.alerts} messagesSent={card.messagesSent}/>
                    </span>
                )}

                {card.lostReason && card.crmStage.startsWith('LOST_') && (
                    <span className="block mt-2 text-[12px] text-neutralBlack-600">{t(`pipelines.lostReason.${card.lostReason}`)}</span>
                )}

                {card.nextAction && (
                    <span className="flex items-center gap-2 mt-3 pt-2.5 border-t border-neutralBlack-100 min-w-0 whitespace-nowrap">
                        <NextActionWho nextAction={card.nextAction} team={team}/>
                        <span className={`text-[12px] truncate min-w-0 ${isWait ? 'text-[#8A5A00] font-medium' : 'text-neutralBlack-700'}`}>
                            {nextActionLabel(card.nextAction, t)}
                        </span>
                        {due && (
                            <span className={`ml-auto flex-none text-[11.5px] ${due.overdue ? 'text-[#B42318] font-semibold' : 'text-neutralBlack-600 font-medium'}`}>
                                {due.text}
                            </span>
                        )}
                    </span>
                )}
                {card.crmStage === 'REACHING_OUT' && card.step && (
                    <span className="block mt-2"><StepDots step={card.step}/></span>
                )}
            </button>

            {/* Keyboard and screen-reader alternative to dragging onto a Lost column. */}
            {!isFinal && (
                <button type="button" onClick={() => onMarkLost(card)}
                        aria-label={t('pipelines.card.moveToLost', {name})} title={t('pipelines.card.moveToLostShort')}
                        className="absolute top-2.5 right-2.5 w-7 h-7 rounded-md flex items-center justify-center text-neutralBlack-600 hover:bg-neutralBlack-100 hover:text-neutralBlack-900 transition-colors duration-200 ease-out">
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/>
                    </svg>
                </button>
            )}
        </div>
    )
}
